import { Button } from '../ui';
import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../api/client';
import { agentLabel } from '../../lib/format';
import { downloadFromResponse, describeDownloadError } from '../../lib/download';

/**
 * Сводка метрик платформы + кнопка скачивания архива наблюдаемости.
 *
 * Читает GET /api/admin/metrics (агрегаты по llm_calls/tool_calls/tasks/
 * sessions/human_requests/session_flags за выбранный период — те же
 * таблицы, что уже пишутся отделами и оркестратором) и отдаёт кнопку,
 * которая скачивает ZIP с теми же агрегатами плюс срез логов процессов за
 * тот же период (см. ExportObservabilityBundleUseCase на бэкенде).
 */

const WINDOWS = [
  { key: '24h', label: '24 часа', hours: 24 },
  { key: '7d', label: '7 дней', hours: 24 * 7 },
  { key: '30d', label: '30 дней', hours: 24 * 30 },
];

function sinceFor(hours) {
  return new Date(Date.now() - hours * 3600 * 1000).toISOString();
}

function fmtNumber(n) {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString('ru-RU');
}

function fmtPercent(ratio) {
  return `${Math.round((ratio || 0) * 100)}%`;
}

function fmtMs(ms) {
  if (!ms) return '0 мс';
  if (ms < 1000) return `${ms} мс`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)} с`;
  return `${(ms / 60000).toFixed(1)} мин`;
}

function StatBox({ label, value }) {
  return (
    <div className="stat-box">
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

function SimpleTable({ columns, rows, emptyText }) {
  if (!rows.length) {
    return <div className="empty" style={{ padding: '12px 0' }}>{emptyText}</div>;
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)' }}>
            {columns.map(c => (
              <th key={c.key} style={{
                padding: '8px 12px', textAlign: c.align || 'left', fontSize: '0.8rem',
                color: 'var(--text-muted)', fontWeight: 600, whiteSpace: 'nowrap',
              }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
              {columns.map(c => (
                <td key={c.key} style={{ padding: '8px 12px', textAlign: c.align || 'left', fontSize: '0.88rem' }}>
                  {c.render ? c.render(row) : row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function MonitoringPanel() {
  const [windowKey, setWindowKey] = useState('7d');
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  const load = useCallback(() => {
    const win = WINDOWS.find(w => w.key === windowKey);
    setLoading(true);
    setError(null);
    api.getPlatformMetrics({ since: sinceFor(win.hours) })
      .then(setMetrics)
      .catch(e => setError(e?.response?.data?.error?.message || 'Не удалось загрузить метрики'))
      .finally(() => setLoading(false));
  }, [windowKey]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async () => {
    const win = WINDOWS.find(w => w.key === windowKey);
    setExporting(true);
    setExportError(null);
    try {
      const response = await api.exportPlatformObservability({ since: sinceFor(win.hours) });
      downloadFromResponse(response, `observability-${windowKey}.zip`);
    } catch (e) {
      setExportError(await describeDownloadError(e));
    } finally {
      setExporting(false);
    }
  };

  if (error && !metrics) return <div className="alert alert-error">{error}</div>;
  if (!metrics) return <div className="loading">Загрузка метрик…</div>;

  const { sessions, tasks, llm, tools, human_requests: humanRequests } = metrics;

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16 }}>
        <div className="flex gap-2">
          {WINDOWS.map(w => (
            <Button
              key={w.key}
              className={`btn btn-sm ${windowKey === w.key ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setWindowKey(w.key)}
            >
              {w.label}
            </Button>
          ))}
        </div>
        <Button className="btn btn-primary" disabled={exporting} onClick={handleExport}>
          {exporting ? 'Собираем архив…' : 'Скачать архив за период'}
        </Button>
      </div>

      {exportError && <div className="alert alert-error" style={{ marginBottom: 16 }}>{exportError}</div>}

      <div className="stats-row">
        <StatBox label="Сессий" value={fmtNumber(sessions.reduce((s, r) => s + r.count, 0))} />
        <StatBox label="Задач" value={fmtNumber(tasks.by_status.reduce((s, r) => s + r.count, 0))} />
        <StatBox label="Провалов задач" value={fmtNumber(tasks.failed)} />
        <StatBox label="Вызовов LLM" value={fmtNumber(llm.calls)} />
        <StatBox label="Токенов" value={fmtNumber(llm.total_tokens)} />
        <StatBox label="Cache-hit" value={fmtPercent(llm.cache_hit_ratio)} />
        <StatBox label="Repair-ratio" value={fmtPercent(llm.repair_ratio)} />
        <StatBox label="Ожидание человека" value={fmtMs(humanRequests.avg_wait_ms)} />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title" style={{ marginBottom: 8 }}>LLM по агентам</div>
        <SimpleTable
          emptyText="Вызовов LLM за период не было."
          columns={[
            { key: 'agent', label: 'Агент', render: r => agentLabel(r.agent) },
            { key: 'calls', label: 'Вызовов', align: 'right', render: r => fmtNumber(r.calls) },
            { key: 'total_tokens', label: 'Токенов', align: 'right', render: r => fmtNumber(r.total_tokens) },
            { key: 'cache_hit_ratio', label: 'Cache-hit', align: 'right', render: r => fmtPercent(r.cache_hit_ratio) },
            { key: 'avg_latency_ms', label: 'Ср. задержка', align: 'right', render: r => fmtMs(r.avg_latency_ms) },
          ]}
          rows={llm.by_agent}
        />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title" style={{ marginBottom: 8 }}>LLM по моделям</div>
        <SimpleTable
          emptyText="Вызовов LLM за период не было."
          columns={[
            { key: 'model', label: 'Модель' },
            { key: 'calls', label: 'Вызовов', align: 'right', render: r => fmtNumber(r.calls) },
            { key: 'total_tokens', label: 'Токенов', align: 'right', render: r => fmtNumber(r.total_tokens) },
          ]}
          rows={llm.by_model}
        />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title" style={{ marginBottom: 8 }}>Инструменты</div>
        <SimpleTable
          emptyText="Вызовов инструментов за период не было."
          columns={[
            { key: 'tool', label: 'Инструмент' },
            { key: 'calls', label: 'Вызовов', align: 'right', render: r => fmtNumber(r.calls) },
            { key: 'failures', label: 'Ошибок', align: 'right', render: r => fmtNumber(r.failures) },
            { key: 'failure_ratio', label: 'Доля ошибок', align: 'right', render: r => fmtPercent(r.failure_ratio) },
            { key: 'avg_duration_ms', label: 'Ср. время', align: 'right', render: r => fmtMs(r.avg_duration_ms) },
          ]}
          rows={tools}
        />
      </div>

      {tasks.top_errors.length > 0 && (
        <div className="card">
          <div className="card-title" style={{ marginBottom: 8 }}>Частые ошибки задач</div>
          <SimpleTable
            columns={[
              { key: 'pattern', label: 'Ошибка' },
              { key: 'count', label: 'Раз', align: 'right', render: r => fmtNumber(r.count) },
            ]}
            rows={tasks.top_errors}
          />
        </div>
      )}
    </div>
  );
}
